import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTION_NAMES } from '@/lib/collections';
import type { ActivityEvent } from '@/shared/types/activityEvent';


/**
 * Hook to enrich activity events with fresh avatar URLs from system_users
 * 
 * This hook takes events from the activity feed and enriches them with
 * the latest avatar URLs from the system_users collection. This ensures:
 * - Avatars reflect the latest data (refreshed nightly)
 * - No redundant data stored in activity_log
 * - system_users is the single source of truth
 * - Efficient batch fetching (one query for all unique users)
 * 
 * @param events - Raw activity events from activity_log
 * @returns Events enriched with current avatar URLs
 * 
 * @example
 * ```tsx
 * const { events } = useActivityFeed({ timeRange: 30 });
 * const enrichedEvents = useActivityFeedWithAvatars(events);
 * ```
 */
export function useActivityFeedWithAvatars(
  events: ActivityEvent[]
): ActivityEvent[] {
  const [avatarMap, setAvatarMap] = useState<Map<string, string>>(new Map());
  const [_loading, setLoading] = useState(false);

  // Extract unique user IDs from events
  const uniqueUserIds = useMemo(() => {
    const userIds = new Set<string>();
    events.forEach(event => {
      if (event.actor?.user_id) {
        userIds.add(event.actor.user_id);
      }
    });
    const uniqueIds = Array.from(userIds);
    return uniqueIds;
  }, [events]);

  // Fetch avatar URLs for all unique users
  useEffect(() => {
    if (uniqueUserIds.length === 0) {
      return;
    }

    const fetchAvatars = async () => {
      setLoading(true);
      try {
        // Separate Firebase UIDs from employee IDs (Firebase UIDs are long alphanumeric strings)
        const firebaseUids: string[] = [];
        const employeeIds: string[] = [];
        
        uniqueUserIds.forEach(id => {
          // Firebase UIDs are typically 28 characters long and alphanumeric
          if (id.length > 20) {
            firebaseUids.push(id);
          } else {
            employeeIds.push(id);
          }
        });

        const newAvatarMap = new Map<string, string>();

        // Query by firebase_uid
        if (firebaseUids.length > 0) {
          const batchSize = 30;
          for (let i = 0; i < firebaseUids.length; i += batchSize) {
            const batch = firebaseUids.slice(i, i + batchSize);
            const systemUsersRef = collection(db, COLLECTION_NAMES.APP_USERS);
            const q = query(
              systemUsersRef,
              where('firebase_uid', 'in', batch)
            );
            
            const querySnapshot = await getDocs(q);
            
            querySnapshot.forEach(doc => {
              const userData = doc.data();
              const firebaseUid = userData.firebase_uid;
              const avatarUrl = userData.employee_data?.cached_avatar_url;
              
              if (firebaseUid && avatarUrl) {
                newAvatarMap.set(firebaseUid, avatarUrl);
              }
            });
          }
        }

        // Query by employee_id
        if (employeeIds.length > 0) {
          const batchSize = 30;
          for (let i = 0; i < employeeIds.length; i += batchSize) {
            const batch = employeeIds.slice(i, i + batchSize);
            
            // Try querying as numbers first
            const batchAsNumbers = batch.map(id => {
              const num = parseInt(id, 10);
              return isNaN(num) ? id : num;
            });
            
            const systemUsersRef = collection(db, COLLECTION_NAMES.APP_USERS);
            const qNumbers = query(
              systemUsersRef,
              where('employee_id', 'in', batchAsNumbers)
            );
            
            const querySnapshotNumbers = await getDocs(qNumbers);
            
            querySnapshotNumbers.forEach(doc => {
              const userData = doc.data();
              const employeeId = userData.employee_id?.toString();
              const avatarUrl = userData.employee_data?.cached_avatar_url;
              
              if (employeeId && avatarUrl) {
                newAvatarMap.set(employeeId, avatarUrl);
              }
            });
            
            // If no results with numbers, try as strings
            if (querySnapshotNumbers.size === 0) {
              const qStrings = query(
                systemUsersRef,
                where('employee_id', 'in', batch)
              );
              
              const querySnapshotStrings = await getDocs(qStrings);
              
              querySnapshotStrings.forEach(doc => {
                const userData = doc.data();
                const employeeId = userData.employee_id?.toString();
                const avatarUrl = userData.employee_data?.cached_avatar_url;
                
                if (employeeId && avatarUrl) {
                  newAvatarMap.set(employeeId, avatarUrl);
                }
              });
            }
          }
        }

        setAvatarMap(newAvatarMap);
      } catch (error) {
        // Don't fail the whole feed if avatars fail to load
      } finally {
        setLoading(false);
      }
    };

    fetchAvatars();
  }, [uniqueUserIds]);

  // Enrich events with avatar URLs
  // Always create new event objects to ensure React detects changes
  const enrichedEvents = useMemo(() => {
    const enriched = events.map(event => {
      const avatarUrl = avatarMap.get(event.actor.user_id);
      return {
        ...event,
        actor: {
          ...event.actor,
          avatar_url: avatarUrl || event.actor.avatar_url
        }
      };
    });
    
    return enriched;
  }, [events, avatarMap]);

  return enrichedEvents;
}